import { useState } from "react";
import {
  View, Text, Pressable,
  TextInput, Modal, ActivityIndicator,
} from "react-native";
import styled from "styled-components/native";
import KeyboardAwareScroll from "@/components/KeyboardAwareScroll";
import { useRouter } from "expo-router";
import { useAuth } from "@/contexts/AuthContext";
import { useClub } from "@/contexts/ClubContext";
import { useMembers, useInvites } from "@/hooks/useMembers";
import { sendInvite, revokeInvite, resendInvite } from "@/lib/invites";
import { doc, updateDoc, deleteDoc, arrayRemove } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { colors, radius, font } from "@/lib/theme";
import type { ClubRole, Member } from "@/lib/types";
import ContentWidth, { ModalContent } from "@/components/ContentWidth";
import { MemberListSkeleton } from "@/components/Skeleton";
import { useTeamConfig } from "@/hooks/useTeamConfig";
import { useConfirm } from "@/hooks/useConfirm";
import ConfirmSheet from "@/components/ConfirmSheet";

const roleColors: Record<string, { bg: string; text: string; border: string }> = {
  admin: { bg: colors.brand50, text: colors.brandDark, border: colors.brandLight },
  player: { bg: colors.bg, text: colors.inkMuted, border: colors.line },
  treasurer: { bg: colors.amber50, text: colors.amber, border: colors.amber200 },
  secretary: { bg: colors.amber50, text: colors.amber, border: colors.amber200 },
  guardian: { bg: colors.blue50, text: colors.blue700, border: colors.blue200 },
};

/* ── Styled Components ── */

/* Screen styled.ScrollView replaced by KeyboardAwareScroll */

const Center = styled.View`
  flex: 1;
  background-color: ${colors.bg};
  justify-content: center;
  align-items: center;
`;

const Header = styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  padding-horizontal: 20px;
  padding-top: 16px;
  padding-bottom: 8px;
`;

const HeaderLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const InviteBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.md}px;
  padding-horizontal: 16px;
  padding-vertical: 10px;
`;

const InviteBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const SectionHeader = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 8px;
  padding-horizontal: 20px;
  padding-top: 20px;
  padding-bottom: 8px;
`;

const SectionLabel = styled.Text`
  font-size: 12px;
  font-weight: ${font.extrabold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const CountBadge = styled.View`
  background-color: ${colors.amber};
  border-radius: ${radius.full}px;
  min-width: 20px;
  height: 20px;
  align-items: center;
  justify-content: center;
  padding-horizontal: 6px;
`;

const CountBadgeText = styled.Text`
  color: #fff;
  font-size: 11px;
  font-weight: ${font.extrabold};
`;

const Card = styled.View`
  margin-horizontal: 20px;
  background-color: ${colors.white};
  border-radius: ${radius.lg}px;
  border-width: 1px;
  border-color: ${colors.line};
  overflow: hidden;
`;

const Row = styled.View<{ bordered?: boolean }>`
  flex-direction: row;
  align-items: center;
  padding-horizontal: 16px;
  padding-vertical: 14px;
  ${(p) => p.bordered ? `border-bottom-width: 1px; border-bottom-color: ${colors.line};` : ""}
`;

const Avatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.brandLight};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const AvatarText = styled.Text`
  font-size: 14px;
  font-weight: ${font.extrabold};
  color: ${colors.brandDark};
`;

const PendingAvatar = styled.View`
  width: 40px;
  height: 40px;
  border-radius: 20px;
  background-color: ${colors.amber50};
  align-items: center;
  justify-content: center;
  margin-right: 12px;
`;

const PendingAvatarText = styled.Text`
  font-size: 16px;
`;

const Flex1 = styled.View`
  flex: 1;
`;

const MemberName = styled.Text`
  font-size: 14px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const MemberSub = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  margin-top: 2px;
`;

const JuniorToggle = styled.Pressable<{ isJunior: boolean }>`
  padding: 4px 10px;
  border-radius: 999px;
  background-color: ${(p) => p.isJunior ? colors.amber50 : colors.bg};
  border: 1px solid ${(p) => p.isJunior ? colors.amber200 : colors.line};
  margin-right: 6px;
`;

const JuniorToggleText = styled.Text<{ isJunior: boolean }>`
  font-size: 10px;
  font-weight: ${font.bold};
  color: ${(p) => p.isJunior ? colors.amber : colors.inkMuted};
`;

const RoleBadge = styled.View<{ bg: string; borderClr: string }>`
  border-radius: ${radius.full}px;
  padding-horizontal: 8px;
  padding-vertical: 3px;
  border-width: 1px;
  background-color: ${(p) => p.bg};
  border-color: ${(p) => p.borderClr};
`;

const RoleBadgeText = styled.Text<{ clr: string }>`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${(p) => p.clr};
`;

const InviteActions = styled.View`
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const ResendBtnWrap = styled.View`
  border-width: 1px;
  border-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 4px;
`;

const ResendBtnText = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.brand};
`;

const RevokeText = styled.Text`
  font-size: 16px;
  color: ${colors.inkMuted};
  font-weight: ${font.bold};
`;

const ModalWrap = styled.View`
  background-color: ${colors.white};
  border-top-left-radius: ${radius.xl}px;
  border-top-right-radius: ${radius.xl}px;
  padding: 24px;
  padding-bottom: 40px;
`;

const ModalTitle = styled.Text`
  font-size: 20px;
  font-weight: ${font.extrabold};
  color: ${colors.ink};
  margin-bottom: 4px;
`;

const ModalDesc = styled.Text`
  font-size: 14px;
  color: ${colors.inkSecondary};
  line-height: 20px;
  margin-bottom: 8px;
`;

const Label = styled.Text`
  font-size: 11px;
  font-weight: ${font.bold};
  color: ${colors.inkMuted};
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 8px;
  margin-top: 16px;
`;

const Input = styled.TextInput`
  background-color: ${colors.bg};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 12px;
  font-size: 15px;
  color: ${colors.ink};
`;

const ErrorBox = styled.View`
  background-color: ${colors.dangerLight};
  border-radius: ${radius.md}px;
  padding-horizontal: 14px;
  padding-vertical: 10px;
  margin-top: 12px;
`;

const ErrorText = styled.Text`
  color: ${colors.danger};
  font-size: 13px;
  font-weight: ${font.semibold};
`;

const SuccessBox = styled.View`
  align-items: center;
  padding-vertical: 20px;
  width: 100%;
`;

const SuccessIcon = styled.Text`
  font-size: 28px;
  margin-bottom: 8px;
`;

const SuccessText = styled.Text`
  font-size: 16px;
  font-weight: ${font.bold};
  color: ${colors.ink};
`;

const CreateBtnWrap = styled.View<{ marginTop?: number }>`
  background-color: ${colors.brand};
  border-radius: ${radius.card}px;
  padding-vertical: 16px;
  align-items: center;
  margin-top: ${(p) => p.marginTop ?? 24}px;
`;

const CreateBtnText = styled.Text`
  color: #fff;
  font-size: 16px;
  font-weight: ${font.bold};
`;

const CancelBtnWrap = styled.View`
  padding-vertical: 12px;
  align-items: center;
  margin-top: 8px;
`;

const CancelBtnText = styled.Text`
  color: ${colors.inkMuted};
  font-size: 15px;
  font-weight: ${font.bold};
`;

const GuardianRow = styled.View`
  padding-horizontal: 16px;
  padding-bottom: 12px;
  padding-top: 4px;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

const GuardianInput = styled.TextInput`
  flex: 1;
  background-color: ${colors.bg};
  border-width: 1px;
  border-color: ${colors.line};
  border-radius: ${radius.sm}px;
  padding-horizontal: 10px;
  padding-vertical: 8px;
  font-size: 13px;
  color: ${colors.ink};
`;

const GuardianSaveBtnWrap = styled.View`
  background-color: ${colors.brand};
  border-radius: ${radius.sm}px;
  padding-horizontal: 12px;
  padding-vertical: 8px;
`;

const GuardianSaveBtnText = styled.Text`
  color: #fff;
  font-size: 12px;
  font-weight: ${font.bold};
`;

const GuardianExisting = styled.Text`
  font-size: 12px;
  color: ${colors.inkMuted};
  font-weight: ${font.medium};
  padding-horizontal: 16px;
  padding-bottom: 12px;
  padding-top: 4px;
`;

const Spacer = styled.View`
  height: 32px;
`;

/* ── Screen ── */

export default function MembersScreen() {
  const router = useRouter();
  const { user } = useAuth();
  const { club, membership } = useClub();
  const { members, loading } = useMembers(club?.id);
  const { invites, pending } = useInvites(club?.id);
  const { confirmProps, confirm } = useConfirm();
  const { isAnyCaptain } = useTeamConfig(club?.id);
  const [showInvite, setShowInvite] = useState(false);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<ClubRole>("player");  // default, hidden from UI
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState("");
  const [sendSuccess, setSendSuccess] = useState(false);
  const [guardianEmails, setGuardianEmails] = useState<Record<string, string>>({});
  const [savingGuardian, setSavingGuardian] = useState<string | null>(null);
  const [roleTarget, setRoleTarget] = useState<Member | null>(null);
  const [search, setSearch] = useState("");
  const [resendState, setResendState] = useState<Record<string, "sending" | "sent">>({});

  const canManage = membership?.role === "admin" || isAnyCaptain(user?.uid);
  const isAdmin = membership?.role === "admin";

  const handleSendInvite = async () => {
    if (!club || !user || !membership || !email.trim()) return;
    setSending(true);
    setSendError("");
    setSendSuccess(false);
    try {
      await sendInvite(club.id, club.name, {
        email: email.trim(),
        role,
        teams: [],
        invitedBy: user.uid,
        invitedByName: membership.displayName,
        clubLogoUrl: club.logoUrl,
      });
      setSendSuccess(true);
      setEmail("");
      setRole("player");
    } catch (err: any) {
      setSendError(err.message || "Failed to send invite.");
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (inv: any) => {
    setResendState((s) => ({ ...s, [inv.id]: "sending" }));
    try {
      await resendInvite(club!.id, inv.id, inv.email, inv.clubName || club!.name, inv.token);
      setResendState((s) => ({ ...s, [inv.id]: "sent" }));
      setTimeout(() => setResendState((s) => { const n = { ...s }; delete n[inv.id]; return n; }), 3000);
    } catch (err: any) {
      console.error("Resend failed:", err);
      setResendState((s) => { const n = { ...s }; delete n[inv.id]; return n; });
    }
  };

  const handleChangeRole = async (memberId: string, newRole: ClubRole) => {
    if (!club) return;
    await updateDoc(doc(db, "clubs", club.id, "members", memberId), { role: newRole });
    setRoleTarget(null);
  };

  const handleRemoveMember = async (member: Member) => {
    if (!club) return;
    // Close the role modal first so the confirm sheet is visible
    setRoleTarget(null);
    // Small delay to let the modal close before showing confirm
    await new Promise((r) => setTimeout(r, 300));
    const ok = await confirm({
      title: `Remove ${member.displayName}?`,
      message: "They will lose access to this club. This cannot be undone.",
      confirmLabel: "Remove",
      destructive: true,
    });
    if (!ok) return;
    await deleteDoc(doc(db, "clubs", club.id, "members", member.id));
    try {
      await updateDoc(doc(db, "users", member.userId), {
        clubIds: arrayRemove(club.id),
      });
    } catch {}
  };

  const handleRevoke = async (invId: string) => {
    const ok = await confirm({ title: "Revoke this invite?", message: "This invite link will no longer work.", confirmLabel: "Revoke", destructive: true });
    if (ok) {
      await revokeInvite(club!.id, invId);
    }
  };

  const handleSaveGuardianEmail = async (memberId: string) => {
    const email = guardianEmails[memberId]?.trim();
    if (!club || !user || !membership || !email) return;
    setSavingGuardian(memberId);
    try {
      await updateDoc(doc(db, "clubs", club.id, "members", memberId), {
        guardianEmail: email,
      });
      await sendInvite(club.id, club.name, {
        email,
        role: "guardian" as ClubRole,
        teams: [],
        invitedBy: user.uid,
        invitedByName: membership.displayName,
        clubLogoUrl: club.logoUrl,
      });
      setGuardianEmails((prev) => ({ ...prev, [memberId]: "" }));
    } catch (err: any) {
      console.error("Guardian invite failed:", err);
    } finally {
      setSavingGuardian(null);
    }
  };

  if (loading) {
    return <View style={{ flex: 1, backgroundColor: colors.bg }}><MemberListSkeleton /></View>;
  }

  return (
    <KeyboardAwareScroll style={{ flex: 1, backgroundColor: colors.bg }} showsVerticalScrollIndicator={false}>
      <ContentWidth>
        {/* Header */}
        <Header>
          <HeaderLabel>{members.length} members</HeaderLabel>
          {canManage && (
            <Pressable style={({ pressed }) => pressed ? { opacity: 0.9 } : {}} onPress={() => router.push("/(app)/invite-member")}>
              <InviteBtnWrap>
                <InviteBtnText>+ Invite</InviteBtnText>
              </InviteBtnWrap>
            </Pressable>
          )}
        </Header>

        {/* Pending invites */}
        {pending.length > 0 && canManage && (
          <>
            <SectionHeader>
              <SectionLabel>Pending invites</SectionLabel>
              <CountBadge><CountBadgeText>{pending.length}</CountBadgeText></CountBadge>
            </SectionHeader>
            <Card>
              {pending.map((inv, i) => (
                <Row key={inv.id} bordered={i < pending.length - 1}>
                  <PendingAvatar>
                    <PendingAvatarText>✉️</PendingAvatarText>
                  </PendingAvatar>
                  <Flex1>
                    <MemberName>{inv.email}</MemberName>
                    <MemberSub>Invited by {inv.invitedByName} · {inv.role}</MemberSub>
                  </Flex1>
                  <InviteActions>
                    <Pressable
                      style={({ pressed }) => pressed ? { opacity: 0.7 } : {}}
                      onPress={() => handleResend(inv)}
                      disabled={!!resendState[inv.id]}
                    >
                      <ResendBtnWrap style={resendState[inv.id] === "sent" ? { borderColor: colors.brand } : {}}>
                        {resendState[inv.id] === "sending" ? (
                          <ActivityIndicator size="small" color={colors.brand} />
                        ) : (
                          <ResendBtnText style={resendState[inv.id] === "sent" ? { color: colors.brand } : {}}>
                            {resendState[inv.id] === "sent" ? "Sent ✓" : "Resend"}
                          </ResendBtnText>
                        )}
                      </ResendBtnWrap>
                    </Pressable>
                    <Pressable onPress={() => handleRevoke(inv.id)}>
                      <RevokeText>✕</RevokeText>
                    </Pressable>
                  </InviteActions>
                </Row>
              ))}
            </Card>
          </>
        )}

        {/* Search */}
        <View style={{ paddingHorizontal: 20, paddingTop: 16 }}>
          <TextInput
            placeholder="Search members..."
            placeholderTextColor={colors.inkMuted}
            value={search}
            onChangeText={setSearch}
            autoCapitalize="none"
            autoCorrect={false}
            style={{
              backgroundColor: colors.white,
              borderWidth: 1,
              borderColor: colors.line,
              borderRadius: radius.lg,
              paddingHorizontal: 14,
              paddingVertical: 10,
              fontSize: 14,
              color: colors.ink,
            }}
          />
        </View>

        {/* Active members */}
        <SectionHeader>
          <SectionLabel>Active</SectionLabel>
        </SectionHeader>
        <Card>
          {members.filter((m) => {
            if (!search.trim()) return true;
            const q = search.toLowerCase();
            return (
              m.displayName?.toLowerCase().includes(q) ||
              m.email?.toLowerCase().includes(q)
            );
          }).map((m, i, filtered) => {
            const rc = roleColors[m.role] || roleColors.player;
            const toggleJunior = async () => {
              if (!club || !canManage) return;
              await updateDoc(doc(db, "clubs", club.id, "members", m.id), {
                isJunior: !m.isJunior,
              });
            };
            return (
              <View key={m.id}>
                <Row bordered={i < filtered.length - 1 && !m.isJunior}>
                  <Avatar>
                    <AvatarText>{m.displayName?.[0]?.toUpperCase() || "?"}</AvatarText>
                  </Avatar>
                  <Flex1>
                    <MemberName>{m.displayName}</MemberName>
                    <MemberSub>{m.email}</MemberSub>
                  </Flex1>
                  {canManage && (
                    <JuniorToggle isJunior={!!m.isJunior} onPress={toggleJunior}>
                      <JuniorToggleText isJunior={!!m.isJunior}>
                        {m.isJunior ? "Junior" : "Senior"}
                      </JuniorToggleText>
                    </JuniorToggle>
                  )}
                  <Pressable
                    disabled={!isAdmin || m.userId === user?.uid}
                    onPress={() => isAdmin && m.userId !== user?.uid && setRoleTarget(m)}
                  >
                    <RoleBadge bg={rc.bg} borderClr={rc.border}>
                      <RoleBadgeText clr={rc.text}>{m.role}</RoleBadgeText>
                    </RoleBadge>
                  </Pressable>
                </Row>
                {canManage && m.isJunior && (
                  m.guardianEmail ? (
                    <GuardianExisting>Guardian: {m.guardianEmail}</GuardianExisting>
                  ) : (
                    <GuardianRow>
                      <GuardianInput
                        placeholder="Guardian email"
                        placeholderTextColor={colors.inkMuted}
                        value={guardianEmails[m.id] || ""}
                        onChangeText={(v: string) => setGuardianEmails((prev) => ({ ...prev, [m.id]: v }))}
                        keyboardType="email-address"
                        autoCapitalize="none"
                      />
                      <Pressable
                        style={({ pressed }) => pressed ? { opacity: 0.9 } : {}}
                        onPress={() => handleSaveGuardianEmail(m.id)}
                        disabled={savingGuardian === m.id || !guardianEmails[m.id]?.trim()}
                      >
                        <GuardianSaveBtnWrap>
                          {savingGuardian === m.id ? (
                            <ActivityIndicator color="#fff" size="small" />
                          ) : (
                            <GuardianSaveBtnText>Invite</GuardianSaveBtnText>
                          )}
                        </GuardianSaveBtnWrap>
                      </Pressable>
                    </GuardianRow>
                  )
                )}
              </View>
            );
          })}
        </Card>
      </ContentWidth>


      {/* Role picker modal */}
      <Modal visible={!!roleTarget} animationType="slide" transparent>
        <ModalContent onClose={() => setRoleTarget(null)}>
          <ModalWrap>
            <ModalTitle>Change role</ModalTitle>
            <ModalDesc>
              Set role for {roleTarget?.displayName || "this member"}.
            </ModalDesc>
            {(["admin", "treasurer", "secretary", "player"] as ClubRole[]).map((r) => {
              const rc = roleColors[r] || roleColors.player;
              const isSelected = roleTarget?.role === r;
              return (
                <Pressable
                  key={r}
                  style={({ pressed }) => ({
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    paddingVertical: 14,
                    paddingHorizontal: 16,
                    borderRadius: 10,
                    marginBottom: 6,
                    backgroundColor: isSelected ? colors.brand50 : colors.bg,
                    borderWidth: 1,
                    borderColor: isSelected ? colors.brand : colors.line,
                    opacity: pressed ? 0.8 : 1,
                  })}
                  onPress={() => roleTarget && handleChangeRole(roleTarget.id, r)}
                >
                  <Text style={{ fontSize: 15, fontWeight: isSelected ? "700" : "500", color: colors.ink, textTransform: "capitalize" }}>
                    {r.replace("-", " ")}
                  </Text>
                  {isSelected && <Text style={{ color: colors.brand, fontWeight: "700" }}>Current</Text>}
                </Pressable>
              );
            })}
            <Pressable
              style={({ pressed }) => pressed ? { opacity: 0.7 } : {}}
              onPress={() => setRoleTarget(null)}
            >
              <CancelBtnWrap>
                <CancelBtnText>Cancel</CancelBtnText>
              </CancelBtnWrap>
            </Pressable>
            {roleTarget && (
              <Pressable
                style={({ pressed }) => ({ marginTop: 8, paddingVertical: 12, alignItems: "center" as const, opacity: pressed ? 0.7 : 1 })}
                onPress={() => roleTarget && handleRemoveMember(roleTarget)}
              >
                <Text style={{ fontSize: 14, color: colors.danger, fontWeight: "600" }}>Remove member</Text>
              </Pressable>
            )}
          </ModalWrap>
        </ModalContent>
      </Modal>

      <Spacer />
      <ConfirmSheet {...confirmProps} />
    </KeyboardAwareScroll>
  );
}
